import { Injectable, OnDestroy } from '@angular/core';
import { filter, map, Observable, of, ReplaySubject, Subject, take } from 'rxjs';

@Injectable()
export class IntersectionService implements OnDestroy {

    private observer?: IntersectionObserver;
    private observingElemsSet: WeakSet<Element> = new WeakSet();
    private intersectedElemsSet: WeakSet<Element> = new WeakSet();

    private initializedSubject: ReplaySubject<void> = new ReplaySubject(1);
    private intersectedSubject: Subject<Element> = new Subject();

    public get initialized(): boolean {
        return this._initialized;
    }
    private _initialized: boolean = false;

    public get initialized$(): Observable<void> {
        return this.initializedSubject.asObservable();
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();

        this.initializedSubject.complete();
        this.intersectedSubject.complete();
    }

    public init(options?: IntersectionObserverInit): void {
        if (this.initialized) {
            throw new Error('[IntersectionService] Initialize called multiple times.');
        }

        this._initialized = true;

        this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), options);
    }

    public observeIntersected(elem: Element): Observable<void> {
        this.assertInitialized();

        if (this.intersectedElemsSet.has(elem)) {
            return of(undefined);
        }

        // FYI: Set methods are idempotent
        this.observingElemsSet.add(elem);
        this.observer.observe(elem);

        return this.intersectedSubject.asObservable()
            .pipe(
                filter(o => o === elem),
                take(1),
                map(() => undefined)
            );
    }

    public unobserveIntersected(elem: Element): void {
        this.assertInitialized();

        this.observingElemsSet.delete(elem);
        this.intersectedElemsSet.delete(elem);
        this.observer.unobserve(elem);
    }

    public isObserving(elem: Element): boolean {
        this.assertInitialized();

        return this.observingElemsSet.has(elem);
    }

    public isIntersected(elem: Element): boolean {
        this.assertInitialized();

        return this.intersectedElemsSet.has(elem);
    }

    private assertInitialized(): void {
        if (!this.initialized) {
            throw new Error('[IntersectionService] Service is not initialized.');
        }
    }

    private intersectionCallback(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            if (!entry.isIntersecting) {
                continue;
            }

            const alreadyIntersected = this.intersectedElemsSet.has(entry.target);

            this.intersectedElemsSet.add(entry.target);
            this.observer.unobserve(entry.target);

            // FYI: Intersection callback can have multiple entries for same element if intersections are fast enough.
            //      Send subject only once.
            if (!alreadyIntersected) {
                this.intersectedSubject.next(entry.target);
            }
        }
    }
}
